import { type BuilderConfig, getDefaultBuilderConfig } from '@ctw/shared/api/builder-config/types';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getStandaloneBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_BUILDER_CONFIG } from '@ctw/shared/utils/query-keys';
import { tw } from '@ctw/shared/utils/tailwind';
import { type PropsWithChildren, createContext, useContext } from 'react';

interface BuilderConfigState extends BuilderConfig {}

const BuilderConfigContext = createContext<BuilderConfigState | null>(null);

interface BuilderConfigProviderProps extends PropsWithChildren {}

export const BuilderConfigProvider = ({ children }: BuilderConfigProviderProps) => {
  const builderConfigQuery = useCtwQuery<BuilderConfig>({
    queryId: QUERY_KEY_BUILDER_CONFIG,
    // Cache builder config for longer than other queries to prevent redundant re-fetches on every
    // navigation.
    staleTime: /* 20 minutes */ 1000 * 60 * 20,
    gcTime: /* 20 minutes */ 1000 * 60 * 20,
    queryFn: async ({ requestContext, ctwFetch, telemetry }) => {
      const response = await ctwFetch(
        `${getStandaloneBaseUrl(requestContext.env)}/api/builders/${requestContext.builderId}/config`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${requestContext.authToken}`,
          },
        },
      );

      if (!response.ok && requestContext.serviceEnv !== 'dev') {
        telemetry.trackError({
          message: 'Failed to fetch builder config',
          error: new Error('Failed to fetch builder config'),
          context: {
            responseData: response.data,
          },
        });
      }

      const defaultBuilderConfig = getDefaultBuilderConfig(requestContext.builderId);

      if (!(response.ok && response.data)) {
        return defaultBuilderConfig;
      }

      const returnedBuilderConfig = response.data as BuilderConfig;

      return {
        ...defaultBuilderConfig,
        ...returnedBuilderConfig,
      } as BuilderConfig;
    },
  });

  if (builderConfigQuery.isError) {
    return (
      <ContentError
        title="Unable to load"
        message="An unknown error occured loading this page."
        className={tw`flex h-full min-h-full w-full min-w-full items-center justify-center`}
      >
        <span>If this problem persists </span>
        <SupportFormLink buttonText="contact support" className={tw`text-base`} />.
      </ContentError>
    );
  }

  if (builderConfigQuery.isLoading || !builderConfigQuery.data) {
    return <LoadingSpinner centered={true} message="Loading..." />;
  }

  return (
    <BuilderConfigContext.Provider value={builderConfigQuery.data}>
      {children}
    </BuilderConfigContext.Provider>
  );
};

export const useBuilderConfig = () => {
  const context = useContext(BuilderConfigContext);

  if (!context) {
    throw new Error('useBuilderConfig must be used within a BuilderConfigProvider');
  }

  return context;
};
